import { ArrowUpDown, MoreHorizontal } from 'lucide-react';
import React from 'react';

import { DataTable } from '@components/dataTable/DataTable';
import { Client } from '@models/client.model';
import MotionButton from '@motion/Button';
import MotionCheckbox from '@motion/Checkbox';
import MotionDropdown from '@motion/Dropdown';
import { Button } from '@shadCN/ui/button';
import {
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@shadCN/ui/dropdown-menu';
import { ColumnDef } from '@tanstack/react-table';

export const columns: ColumnDef<Client>[] = [
  {
    id: 'select',
    header: ({ table }) => (
      <MotionCheckbox
        isChecked={
          table.getIsAllPageRowsSelected() ||
          !!(table.getIsSomePageRowsSelected() && 'indeterminate')
        }
        setIsChecked={value => table.toggleAllPageRowsSelected(!!value)}
      />
    ),
    cell: ({ row }) => (
      <MotionCheckbox
        isChecked={row.getIsSelected()}
        setIsChecked={value => row.toggleSelected(!!value)}
      />
    ),
    enableSorting: false,
    enableHiding: false,
  },
  {
    accessorKey: 'name',
    header: ({ column }) => <div className="px-3">Name</div>,
    enableHiding: false,
    filterFn: (row, _, filterValue) => {
      const primaryContact = row.original.primaryContact;
      const name = `${primaryContact.title} ${primaryContact.firstName} ${primaryContact.lastName}`;
      return name.toLowerCase().includes(filterValue.toLowerCase());
    },
    cell: ({ row }) => {
      const primaryContact = row.original.primaryContact;
      return (
        <div className="px-3 capitalize">
          {primaryContact.title} {primaryContact.firstName}{' '}
          {primaryContact.lastName}
        </div>
      );
    },
  },
  {
    accessorKey: 'email',
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}>
          Email
          <ArrowUpDown />
        </Button>
      );
    },
    cell: ({ row }) => (
      <div className="px-3">{row.original.primaryContact.email}</div>
    ),
  },
  {
    accessorKey: 'address',
    header: ({ column }) => <div className="px-3">Address</div>,
    cell: ({ row }) => (
      <div className="px-3">{row.original.primaryContact.address}</div>
    ),
  },
  {
    accessorKey: 'phone',
    header: ({ column }) => <div className="px-3">Phone</div>,
    cell: ({ row }) => (
      <div className="px-3">{row.original.primaryContact.phone}</div>
    ),
  },
  {
    id: 'actions',
    enableHiding: false,
    cell: ({ row }) => {
      return (
        <MotionDropdown
          triggerButtonProps={{ variant: 'ghost' }}
          triggerButtonContent={<MoreHorizontal />}
          dropdownContent={[
            <DropdownMenuLabel>Actions</DropdownMenuLabel>,
            <DropdownMenuItem
              onClick={() => navigator.clipboard.writeText('payment.id')}>
              Copy payment ID
            </DropdownMenuItem>,
            <DropdownMenuSeparator />,
            <DropdownMenuItem>View customer</DropdownMenuItem>,
            <DropdownMenuItem>View payment details</DropdownMenuItem>,
          ]}
        />
      );
    },
  },
];

interface Props {
  clients: Client[];
}

const ClientsTable: React.FC<Props> = ({ clients }) => {
  const CSSBlock = 'clients-table';

  return (
    <div className={`${CSSBlock}`}>
      <DataTable<Client>
        columns={columns}
        data={clients}
        searchColumns={['name']}
        searchPlaceholder="Filter Clients"
        toolbarButtons={[
          <MotionButton key="add-client">Add Client</MotionButton>,
        ]}
      />
    </div>
  );
};

export default ClientsTable;
